<template>
  <div
    v-bind="$attrs"
    class="gmf-avatar gmf-avatar-component"
    :class="classes"
    @click="onClick"
    v-if="! reloading"
  >
    <!-- initials_and_2d -->
    <template v-if="useInitialsAndAvatar">
      <object v-if="useObjectHack" class="gmf-avatar-content" type="image/svg+xml" :data="avatarUrl || placeholderUrl" title="Avatar"></object>
      <img v-else class="gmf-avatar-content" :src="avatarUrl || placeholderUrl" alt="Avatar" />
      <span class="gmf-avatar-initials-container">
        <span>{{ customerInitials }}</span>
      </span>
    </template>

    <!-- initials -->
    <span v-else-if="useInitials" class="gmf-avatar-initials-container">
      <span>{{ customerInitials }}</span>
    </span>

    <!-- 3D -->
    <Image
      v-else-if="use3DAvatar"
      :src="avatarUrl + overrideQuery"
      :highSrc="computedHighSrc"
      :lazy="lazy && computedHighSrc"
      classes="gmf-avatar-content gmf-avatar-background"
      alt="Avatar"
    />

    <!-- enabled || placeholder_only -->
    <object v-else-if="useObjectHack" class="gmf-avatar-content" type="image/svg+xml" :data="avatarUrl || placeholderUrl" title="Avatar"></object>
    <img v-else class="gmf-avatar-content" :src="avatarUrl || placeholderUrl" alt="Avatar" />

    <div v-if="customizable" class="gmf-avatar-customizable">
      <Icon icon="change_avatar" />
    </div>
    <div v-if="showIndicator" :class="{ 'gmf-offline': ! isOnline }" class="gmf-status-indicator"/>
</div>
</template>

<style>
.gmf-context .gmf-avatar {
  aspect-ratio: 1;
  background: var(--gmf-avatar-bg);
  border-radius: 100%;
}
.gmf-avatar-component {
  line-height: 0 !important;
  position: relative;
}
.gmf-avatar-component::after { /* Old safari fix */
  display: block;
  content: "";
  padding-bottom: 100%;
}

.gmf-avatar-component.gmf-avatar-popout:has(.gmf-avatar-loading) {
  background: none;
  background-color: inherit;
}

.gmf-avatar-content {
  width: 100%;
  height: 100%;
  position: absolute;
  inset: 0;
  border-radius: 100%;
}
.gmf-status-indicator.gmf-offline::before {
  content: v-bind('lastOnline');
}
.gmf-avatar-customizable {
  --gmf-status-position: 135deg;

  transform-origin: top left;
  transform: rotate(var(--gmf-status-position));

  top: 50%;
  left: 50%;
  position: absolute;

  width: 50%;
}

.gmf-avatar-customizable .gmf-icon {
  top: 0;
  left: 100%;
  height: 25px;
  min-width: 25px;
  position: absolute;
  background: #EFEFF0;
  border-radius: 100%;
  border: 2px solid var( --gmf-col-white);
  transform: translate(-50%, -50%) rotate(calc(-1 * var(--gmf-status-position)));
}


.gmf-avatar-popout .gmf-avatar-content {
  left: -15%;
  right: -15%;
  top: -25%;
  bottom: -5%;
  width: 130%;
  height: 130%;
  max-width: unset;
  max-height: unset;
  background: none !important;
  border-radius: 100%;
}

.gmf-avatar-popout.gmf-avatar-3d-fullbody .gmf-avatar-content {
  left: -50%;
  right: -50%;
  top: -60%;
  bottom: -40%;

  height: 200%;
  width: auto;
  margin: auto;
  border-radius: unset;
}

.gmf-context .gmf-avatar-initials-container {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  container-type: size;
}
.gmf-avatar-initials-container .gmf-avatar-content {
  position: absolute; 
  inset: 0;
}
.gmf-context .gmf-avatar-initials-container span {
  letter-spacing: 1px;
  padding-left: 1px;
  text-transform: uppercase;
  font-family: var(--gmf-header-font);
  color: var(--gmf-col-darkgray);
  font-size: 18px;
  font-size: 40cqw;
  line-height: 40cqw;
  font-weight: bold;
}

.gmf-avatar-3d.gmf-avatar-placeholder .gmf-avatar-content {
  filter: brightness(0) invert(0.8);
}
</style>

<script>
import state from '@/state';
import Image from '@/components/Image';
import eventbus from '@/utils/eventbus';
import openInModal from '@/legacy/avatar-configurator/openInModal';
import openPopupProfile from '@/components/PopupProfileCard/openPopupProfileCard';
import openAvatarConfigurator from '@/components/AvatarConfigurator/openAvatarConfigurator';
import Icon from '@/components/Icon';
import isSafari from '@/utils/isSafari';

/* @TODO add way of disabling clicks */
export default {
  components: { Image, Icon },
  props: {
    view: { // 3d avatar view
      type: String,
      default: 'headshot',
    },
    customer: {
      type: Object,
      require: true,
    },
    disablePopup: {
      type: Boolean,
      default: false,
    },
    disableObjectHack: {
      type: Boolean,
      default: false,
    },
    disableOnlineIndicator: {
      type: Boolean,
      default: false,
    },
    customizable: { // Add cog wheel to avatar and @click on avatar opens configurator.
      type: Boolean,
      default: false,
    },
    defaultPopupTab: {
      type: String,
      default: 'profile',
    },
    disablePopout: { // Disables the avatar sticking out of its circle
      type: Boolean,
      default: false,
    },
    animated: {
      type: Boolean,
      default: false,
    },
    lazy: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      state,
      reloading: false,
      imgLoading: null, // Avatar 3D image loading state.
      show3DAvatar: true,
      tempDisableAnim: false,
    };
  },
  mounted () {
    eventbus.on('avatar:update', this.updateAvatarCode);
  },
  unmounted () {
    eventbus.off('avatar:update', this.updateAvatarCode);
  },
  computed: {
    computedHighSrc () {
      return this.useAnimated3DAvatars && ! this.tempDisableAnim && this.animatedAvatarUrl && (this.animatedAvatarUrl + this.overrideQuery) || null;
    },
    overrideQuery () {
      const ts = state.avatarTimestamps[this.customer.id];
      if (! ts) {
        return '';
      }
      return '?ts=' + ts;
    },
    anonymousInitial () {
      return state.t('comments.anonymous')[0].toUpperCase() || '?';
    },
    customerInitials () {
      if (! this.customer) {
        return this.anonymousInitial;
      }
      const firstNameInitials = (this.customer.firstName || this.customer.name || '').trim().split(' ').filter(n => n).map(n => n.trim()[0].toUpperCase());
      const lastNameInitials = (this.customer.lastName || '').trim().split(' ').filter(n => n).map(n => n.trim()[0].toUpperCase());

      if (lastNameInitials.length > 0) {
        return firstNameInitials[0] + lastNameInitials[0];
      } else if (firstNameInitials.length > 1) {
        return firstNameInitials[0] + firstNameInitials[1];
      } else {
        if (firstNameInitials.length === 0) {
          return this.anonymousInitial;
        }
        return firstNameInitials[0];
      }
    },
    classes () {
      const classes = {
        'gmf-clickable-wrapper': this.isClickable,
        'gmf-avatar-3d': this.use3DAvatar,
        'gmf-avatar-popout': this.use3DAvatar && ! this.disablePopout,
        'gmf-avatar-initials': this.useInitials,
      };

      if (this.use3DAvatar) {
        classes['gmf-avatar-3d-' + this.view] = true;
      }

      if (this.usingPlaceholder) {
        classes['gmf-avatar-placeholder'] = true;
      }

      return classes;
    },
    merchantId () {
      return this.customer?.merchantId || state.merchantId;
    },
    usingPlaceholder () {
      return this.avatarUrl === this.placeholderUrl;
    },
    placeholderUrl () {
      if (this.use3DAvatar) {
        return 'https://cdn.gamifiera.com/v2/avatar/headshot/random/random';
      }
      return state.merchantConfig.avatars.placeholderImage;
    },
    useObjectHack () {
      return isSafari && ! this.disableObjectHack;
    },
    isOnline() {
      if (! this.customer) {
        return false;
      }
      if (this.profile?.id === this.customer.id) {
        return true;
      }

      return this.customer.isOnline;
    },
    showIndicator () {
      if (! state.merchantConfig?.widgets?.showOnlineStatus || this.disableOnlineIndicator) {
        return false;
      }

      return (this.isOnline || this.customer && this.customer.lastOnline);
    },
    popupProfileCardEnabled () {
      if (! this.publicProfile || this.disablePopup) {
        return false;
      }
      return !!state.merchantConfig?.popupProfileCard?.enabled;
    },
    publicProfile () {
      const a = this.customer;

      if (! a) {
        return false;
      }

      if (a.merchantId && a.merchantId !== state.merchantId) {
        return false;
      }
      return ! a.isGuest;
    },
    useInitials () {
      return state.merchantConfig.avatars.enabled === 'initials';
    },
    useInitialsAndAvatar () {
      return state.merchantConfig.avatars.enabled === 'initials_and_2d';
    },
    use3DAvatar () {
      return state.merchantConfig.avatars.enabled === '3D';
    },
    useAnimated3DAvatars () {
      const a = this.customer;

      if (a) {
        if (a.isGuest) return false;
        if (a.merchantId && a.merchantId !== state.merchantId) return false;
      }
      return state.merchantConfig.avatars.animation?.enabled && this.animated;
    },
    indicatorStyling () {
      return {
        background: this.isOnline ? 'var(--gmf-col-online)' : 'var(--gmf-col-darkgray)',
      }
    },
    lastOnline () {
      if (this.isOnline || ! this.customer || ! this.customer.lastOnline || this.customer.lastOnline <= 0) {
        return '""';
      }

      const d = Math.floor(this.customer.lastOnline / 60 / 24);
      const h = Math.floor(this.customer.lastOnline / 60);
      const m = this.customer.lastOnline % 60;
      const time = d ? d + 'd' : (h ? h + 'h' : m + 'm');
      return `"${time}"`;
    },
    isClickable () {
      return this.customizable || this.popupProfileCardEnabled;
    },
    profile() {
      return this.state.profile;
    },
    avatarUrl () {
      if (! this.customer) {
        // @TODO special placeholder for 3d avatar guests?
        return this.placeholderUrl;
      }
      if (this.use3DAvatar) {
        return `${state.cdnBase}/v2/avatar/${this.view}/${this.merchantId}/${this.customer?.id}`;
      }
      if (this.customer?.avatarUrl) {
        return this.customer.avatarUrl;
      }
      const url = `${state.cdnBase}/v1/avatar/render/${this.customer.avatarCode}`;
      return this.customer.avatarCode ? url : this.placeholderUrl;
    },
    animatedAvatarUrl () {
      return `${state.cdnBase}/v2/animated-avatar/${this.view}/${this.merchantId}/${this.customer.id}`;
    },
  },
  methods: {
    onClick (ev) {
      if (this.customizable) {
        const editorUrl = state.merchantConfig?.profileCard?.avatarEditorUrl;
        if (editorUrl) {
          window.location.href = editorUrl;
          return false;
        }

        if (this.use3DAvatar) {
          openAvatarConfigurator();
        } else {
          openInModal();
        }
        return false;
      }

      if (this.popupProfileCardEnabled) {
        this.openProfileCard(ev);
        return false;
      } 

      return true;
    },
    openProfileCard (ev) {
      ev.stopPropagation();
      ev.preventDefault();
      openPopupProfile(this.customer.id, this.defaultPopupTab);
    },
    updateAvatarCode () {
      if (this.profile?.id === this.customer.id) {
        this.swapImgInDOM();
      }
    },
    swapImgInDOM () {
      this.reloading = true;
      if (isSafari) {
        setTimeout(() => {
          this.reloading = false;
        }, 500);
      } else {
        this.$nextTick(() => {
          this.reloading = false;
        });
      }
    },
  },
  watch: {
    reloading() {
      this.$emit("update:loading", this.reloading);
    }
  }
};
</script>
